const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

/**
 * Project a point onto a line segment and return the closest point on the segment.
 */
const projectPointOntoSegment = (px, py, ax, ay, bx, by) => {
  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return { x: ax, y: ay };
  const t = clamp(((px - ax) * dx + (py - ay) * dy) / lenSq, 0, 1);
  return { x: ax + t * dx, y: ay + t * dy };
};

/**
 * Compute the SVG polygon points for a speech bubble tail triangle.
 *
 * @param {number} width   - bubble width
 * @param {number} height  - bubble height
 * @param {number} tipX    - tail tip X relative to bubble top-left
 * @param {number} tipY    - tail tip Y relative to bubble top-left
 * @param {number} baseWidth - width of the tail base on the bubble edge
 * @returns {{ base1: {x,y}, tip: {x,y}, base2: {x,y}, edge: string } | null}
 *          Returns null if tip is inside the bubble rect.
 */
export const computeTailTriangle = (width, height, tipX, tipY, baseWidth) => {
  const margin = 2;
  // If tip is inside the bubble, return null
  if (tipX >= margin && tipX <= width - margin && tipY >= margin && tipY <= height - margin) {
    return null;
  }

  // Four edges as segments
  const edges = [
    { name: "top", ax: 0, ay: 0, bx: width, by: 0 },
    { name: "bottom", ax: 0, ay: height, bx: width, by: height },
    { name: "left", ax: 0, ay: 0, bx: 0, by: height },
    { name: "right", ax: width, ay: 0, bx: width, by: height },
  ];

  // Find nearest edge to the tip
  let bestEdge = null;
  let bestDist = Infinity;
  let bestProj = null;

  for (const edge of edges) {
    const proj = projectPointOntoSegment(tipX, tipY, edge.ax, edge.ay, edge.bx, edge.by);
    const dx = tipX - proj.x;
    const dy = tipY - proj.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < bestDist) {
      bestDist = dist;
      bestEdge = edge;
      bestProj = proj;
    }
  }

  if (!bestEdge) return null;

  // Compute base points offset along the edge
  const edgeDx = bestEdge.bx - bestEdge.ax;
  const edgeDy = bestEdge.by - bestEdge.ay;
  const edgeLen = Math.sqrt(edgeDx * edgeDx + edgeDy * edgeDy);
  if (edgeLen === 0) return null;

  const ux = edgeDx / edgeLen;
  const uy = edgeDy / edgeLen;
  const halfBase = baseWidth / 2;

  let base1 = { x: bestProj.x - ux * halfBase, y: bestProj.y - uy * halfBase };
  let base2 = { x: bestProj.x + ux * halfBase, y: bestProj.y + uy * halfBase };

  // Clamp base points to stay on the edge segment
  const clampToEdge = (pt) => {
    const proj = projectPointOntoSegment(pt.x, pt.y, bestEdge.ax, bestEdge.ay, bestEdge.bx, bestEdge.by);
    return proj;
  };

  base1 = clampToEdge(base1);
  base2 = clampToEdge(base2);

  return {
    base1,
    tip: { x: tipX, y: tipY },
    base2,
    edge: bestEdge.name,
  };
};

/**
 * Normalize tails: convert single-tail fields into a tails array.
 */
export const normalizeTails = (bubble) => {
  if (Array.isArray(bubble.tails) && bubble.tails.length > 0) {
    return bubble.tails;
  }
  // Legacy single tail
  if (bubble.tailTipX != null && bubble.tailTipY != null) {
    return [{
      id: "tail-0",
      tipX: bubble.tailTipX,
      tipY: bubble.tailTipY,
      width: bubble.tailWidth || 20,
      style: "pointed",
    }];
  }
  if (bubble.tailPlacement) {
    const coords = migrateTailPlacement(bubble.tailPlacement, bubble.width || 160, bubble.height || 96);
    return [{
      id: "tail-0",
      tipX: coords.tailTipX,
      tipY: coords.tailTipY,
      width: bubble.tailWidth || 20,
      style: "pointed",
    }];
  }
  return [{
    id: "tail-0",
    tipX: (bubble.width || 160) / 2,
    tipY: (bubble.height || 96) + 30,
    width: 20,
    style: "pointed",
  }];
};

export const TAIL_STYLES = [
  { key: "pointed", label: "Pointed" },
  { key: "right-left", label: "Right angle (left)" },
  { key: "right-right", label: "Right angle (right)" },
  { key: "thought", label: "Thought circles" },
  { key: "jagged", label: "Jagged/Lightning" },
  { key: "curved", label: "Curved" },
];

/**
 * Convert legacy tailPlacement enum to tip coordinates.
 */
export const migrateTailPlacement = (placement, width, height) => {
  const offset = 30;
  switch (placement) {
    case "top":
      return { tailTipX: width / 2, tailTipY: -offset };
    case "left":
      return { tailTipX: -offset, tailTipY: height / 2 };
    case "right":
      return { tailTipX: width + offset, tailTipY: height / 2 };
    case "bottom":
    default:
      return { tailTipX: width / 2, tailTipY: height + offset };
  }
};
